const DAY = 86_400_000;
const PAGE_SIZE = 100;
const MAX_PAGES = 200;
const SORT_KEYS = new Set(["date", "symbol", "status", "time", "eps", "source"]);

export class UpcomingEarningsDataSource {
  constructor({ endpoint = "/api/v1/upcoming-earnings", fetchImpl = globalThis.fetch } = {}) {
    this.endpoint = endpoint;
    this.fetchImpl = fetchImpl.bind(globalThis);
  }

  async load({ from, to, signal, onProgress = () => {} }) {
    validateDateRange(from, to);
    const items = [];
    const seenIds = new Set();
    const seenSymbolIds = new Set();
    let offset = 0;
    let metadata = null;
    for (let pageNumber = 0; pageNumber < MAX_PAGES; pageNumber += 1) {
      signal?.throwIfAborted();
      const base = globalThis.location?.href || "http://localhost/";
      const url = new URL(this.endpoint, base);
      url.searchParams.set("from", from);
      url.searchParams.set("to", to);
      url.searchParams.set("offset", String(offset));
      url.searchParams.set("limit", String(PAGE_SIZE));
      const response = await this.fetchImpl(url.toString(), {
        signal, credentials: "same-origin", headers: { Accept: "application/json" }
      });
      signal?.throwIfAborted();
      if (!response.ok) {
        throw new Error(response.status === 401 || response.status === 403
          ? "Sign in to view upcoming earnings."
          : "Upcoming earnings could not be loaded. Please try again.");
      }
      let page;
      try { page = await response.json(); }
      catch { throw new Error("The earnings response was incomplete. Please try again."); }
      signal?.throwIfAborted();
      if (!page || !Array.isArray(page.items) || !Number.isSafeInteger(page.totalCount)
        || page.totalCount < 0 || page.offset !== offset || page.items.length > PAGE_SIZE
        || page.from !== from || page.to !== to) {
        throw new Error("The earnings response was incomplete. Please try again.");
      }
      if (metadata && page.totalCount !== metadata.totalCount) {
        throw new Error("The earnings calendar changed while loading. Please try again.");
      }
      if (!metadata) {
        const { items: ignored, ...summary } = page;
        metadata = summary;
      }
      for (const item of page.items) {
        if (!item || typeof item.id !== "string" || !item.id
          || typeof item.symbolId !== "string" || !item.symbolId
          || typeof item.symbol !== "string" || !item.symbol
          || typeof item.companyName !== "string"
          || !validDate(item.expectedAnnouncementDate)
          || item.expectedAnnouncementDate < from || item.expectedAnnouncementDate > to
          || seenIds.has(item.id) || seenSymbolIds.has(item.symbolId)) {
          throw new Error("The earnings response was incomplete. Please try again.");
        }
        seenIds.add(item.id);
        seenSymbolIds.add(item.symbolId);
        items.push(item);
      }
      if (items.length > page.totalCount) throw new Error("The earnings calendar changed while loading. Please try again.");
      onProgress({ loaded: items.length, total: page.totalCount });
      if (page.nextOffset === null) {
        if (items.length !== page.totalCount) throw new Error("The earnings response was incomplete. Please try again.");
        return { items, metadata };
      }
      if (!Number.isSafeInteger(page.nextOffset) || page.nextOffset !== offset + page.items.length
        || page.items.length === 0 || page.nextOffset >= page.totalCount) {
        throw new Error("The earnings response was incomplete. Please try again.");
      }
      offset = page.nextOffset;
    }
    throw new Error("This date range has too many results. Choose a shorter date range.");
  }
}

export function marketDate(now = new Date()) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit"
  }).formatToParts(now);
  const get = (key) => parts.find((part) => part.type === key)?.value;
  return `${get("year")}-${get("month")}-${get("day")}`;
}

export function presetDateRange(preset = "30days", now = new Date()) {
  const from = marketDate(now);
  const date = new Date(`${from}T00:00:00Z`);
  if (preset === "today") return { from, to: from };
  if (preset === "week") {
    return { from, to: isoDate(new Date(date.getTime() + ((7 - date.getUTCDay()) % 7) * DAY)) };
  }
  if (preset === "12months") {
    const next = new Date(date);
    next.setUTCFullYear(next.getUTCFullYear() + 1, next.getUTCMonth(), 1);
    const lastDay = new Date(Date.UTC(next.getUTCFullYear(), next.getUTCMonth() + 1, 0)).getUTCDate();
    next.setUTCDate(Math.min(date.getUTCDate(), lastDay));
    return { from, to: isoDate(next) };
  }
  return { from, to: isoDate(new Date(date.getTime() + 29 * DAY)) };
}

export function validateDateRange(from, to) {
  if (!validDate(from) || !validDate(to)) throw new Error("Enter a valid From and To date.");
  if (to < from) throw new Error("The To date must be on or after the From date.");
  if ((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY > 3660) {
    throw new Error("Choose a date range of 3,660 days or fewer.");
  }
}

export function filterAndSortEarnings(items, {
  query = "", confirmedOnly = false, sortKey = "date", sortDirection = "asc"
} = {}) {
  const search = String(query).trim().toLocaleLowerCase();
  const key = SORT_KEYS.has(sortKey) ? sortKey : "date";
  const direction = sortDirection === "desc" ? -1 : 1;
  const filtered = items.filter((item) => (!confirmedOnly || item.isConfirmed === true)
    && (!search || `${item.symbol} ${item.companyName}`.toLocaleLowerCase().includes(search)));
  const compareText = (a, b) => String(a || "").localeCompare(String(b || ""), "en", { numeric: true });
  const value = (item) => ({
    date: item.expectedAnnouncementDate, symbol: item.symbol,
    status: item.isConfirmed === true ? "Issuer confirmed" : "Estimated",
    time: item.reportTime, eps: item.estimatedEps, source: item.publisherName || item.source
  })[key];
  return filtered.sort((a, b) => {
    const left = value(a); const right = value(b);
    if (key === "eps" && (left == null || right == null)) {
      if (left == null && right != null) return 1;
      if (right == null && left != null) return -1;
    }
    const primary = key === "eps" && left != null && right != null
      ? Number(left) - Number(right) : compareText(left, right);
    return primary * direction
      || compareText(a.expectedAnnouncementDate, b.expectedAnnouncementDate)
      || compareText(a.symbol, b.symbol) || compareText(a.id, b.id);
  });
}

function validDate(value) {
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)
    && Number.isFinite(Date.parse(`${value}T00:00:00Z`))
    && isoDate(new Date(`${value}T00:00:00Z`)) === value;
}
function isoDate(value) { return value.toISOString().slice(0, 10); }
